import React, { useEffect, useRef, useState } from "react";
import { format } from "date-fns";
import { kn } from "date-fns/locale";
import { object, bool } from "prop-types";
import { throttle } from "lodash";
import { useSelector } from "react-redux";
import get from "lodash/get";
import Author from "../../../atoms/author";
import FollowUs from "../../../atoms/follow-story-page";
import BookmarkCommentShareButtons from "../../../atoms/bookmark-comment-share-buttons";
import { AdContainer } from "../../../ads";

import "./author-and-timestamp.m.css";

const AuthorAndTimeStamp = ({ story, isPVExclusive }) => {
  const hiddenAuthorIds = useSelector((state) =>
    get(state, ["qt", "config", "publisher-attributes", "hidden_author_ids"], [])
  );
  const hiddenIdsList = Array.isArray(hiddenAuthorIds)
    ? hiddenAuthorIds.map(String)
    : hiddenAuthorIds
    ? [String(hiddenAuthorIds)]
    : [];
  const bylineAuthorName = story?.metadata?.["byline-no-author-page"];

  const primaryAuthorId = get(story, ["authors", "0", "id"], null);
  const isPrimaryAuthorHidden = primaryAuthorId ? hiddenIdsList.includes(String(primaryAuthorId)) : false;

  const authorsToDisplay = story.authors
    ? story.authors.filter((author) => !hiddenIdsList.includes(String(author.id)))
    : [];
  const hasVisibleAuthors = isPrimaryAuthorHidden
    ? false
    : authorsToDisplay.length > 0 || (bylineAuthorName && bylineAuthorName.length > 0);

  const [isMobile, setMobile] = useState(false);
  const [stickyFollowUs, setStickyFollowUs] = useState(false);
  const [followusWidth, setFollowusWidth] = useState("auto");
  const followusRef = useRef(null);

  useEffect(() => {
    if (window && window.innerWidth < 1024) {
      setMobile(true);
    } else {
      setMobile(false);
    }
  }, []);

  useEffect(() => {
    const desktopHeader = document.querySelector("#desktop-sticky-header");
    const timestampWrapper = document.querySelector("#timestamp-container");
    const exclusiveWrapper = document.querySelector("#exclusive-author-wrapper");
    const stickyShare = document.querySelector(".sticky-share");
    const stickyShareTop = stickyShare ? stickyShare.getBoundingClientRect().top + window.pageYOffset : 0;

    let previousScrollY = window.scrollY;

    function followusBarStickyHandler() {
      if (typeof window === "undefined") return;
      const currentScrollY = window.scrollY;
      const scrollPosition = window.scrollY + window.innerHeight;
      const pageHeight = document.documentElement.scrollHeight;

      const isAtPageBottom = scrollPosition >= pageHeight;
      const desktopHeaderBottom = desktopHeader?.getBoundingClientRect().bottom;
      const timestampBottom = timestampWrapper?.getBoundingClientRect().bottom;
      const exclusiveTop = exclusiveWrapper?.getBoundingClientRect().top;

      const taboolaAdWrapper = document.querySelector("#ad-wrapper-taboola");
      const tagsContainer = document.querySelector("#tags-container");

      if (tagsContainer) {
        let tagsContainerTop = tagsContainer.getBoundingClientRect().top;
        tagsContainerTop -= isMobile ? 10 : isPVExclusive ? 60 : 20;

        const stickyShareHeight = stickyShare?.getBoundingClientRect().height || 0;

        if (tagsContainerTop <= stickyShareHeight) {
          setStickyFollowUs(false);
          previousScrollY = currentScrollY;
          return;
        }
      }

      if (!["listicle", "live-blog"].includes(story["story-template"]) && taboolaAdWrapper) {
        let taboolaAdTop = taboolaAdWrapper.getBoundingClientRect().top;
        taboolaAdTop -= isMobile ? 60 : isPVExclusive ? 180 : 120;

        const stickyShareHeight = stickyShare?.getBoundingClientRect().height || 0;

        if (taboolaAdTop <= stickyShareHeight) {
          setStickyFollowUs(false);
          previousScrollY = currentScrollY;
          return;
        }
      }

      if (isAtPageBottom) {
        setStickyFollowUs(true);
      } else if (isMobile) {
        if (currentScrollY >= stickyShareTop) {
          setStickyFollowUs(true);
          stickyShare.style.top = "0px";
          stickyShare.classList.remove("move-down-sticky-share");
          if (currentScrollY < previousScrollY) {
            stickyShare.classList.add("move-down-sticky-share");
          }
        } else {
          if (currentScrollY < previousScrollY && currentScrollY <= stickyShareTop - 35) {
            setStickyFollowUs(false);
          }
        }
      } else if (isPVExclusive) {
        setStickyFollowUs(desktopHeaderBottom >= exclusiveTop);
      } else {
        setStickyFollowUs(desktopHeaderBottom >= timestampBottom);
      }
      previousScrollY = currentScrollY;
    }

    window.addEventListener("scroll", throttle(followusBarStickyHandler, 50));

    return () => {
      window.removeEventListener("scroll", followusBarStickyHandler);
    };
  }, [isMobile, isPVExclusive]);

  useEffect(() => {
    if (followusRef.current) {
      setFollowusWidth(`${followusRef.current.offsetWidth}px`);
    }
  }, [stickyFollowUs, isMobile]);

  const publishedDateAndTime = format(new Date(story["first-published-at"]), "d MMMM yyyy, H:mm", {
    locale: kn
  }).replace("ಫೆಬ್ರವರಿ", "ಫೆಬ್ರುವರಿ");

  const lastUpdatedDateAndTime = format(new Date(story["last-published-at"]), "d MMMM yyyy, H:mm", {
    locale: kn
  }).replace("ಫೆಬ್ರವರಿ", "ಫೆಬ್ರುವರಿ");

  const AuthorTimeStampAndIcons = (
    <div styleName="wrapper">
      {hasVisibleAuthors && (
        <div styleName="author-name">
          <Author authors={authorsToDisplay} story={story} clazzName="author-name-image" />
        </div>
      )}
      <div styleName="timestamp" id="timestamp-container">
        <>
          <div styleName="date">Published : {publishedDateAndTime} IST</div>
          {story["owner-name"].toLowerCase() !== "migrator" && (
            <>
              <div styleName="short-line"></div>
              <div styleName="date">Last Updated : {lastUpdatedDateAndTime} IST</div>
            </>
          )}
        </>
      </div>
      <div styleName="ad-wrapper" className="hide-desktop">
        <AdContainer
          AdClassName="standard-300x250"
          id="div-gpt-ad-1770621030464-0"
          desktop={false}
          mobile="PV_Mweb_AT_Top_300x250"
        />
      </div>
      <div styleName={`${stickyFollowUs ? "alternateDiv-Sticky" : ""}`}></div>
      <div
        styleName={`follow-us-wrapper ${stickyFollowUs ? "sticky-followus" : ""}`}
        className="sticky-share"
        ref={followusRef}
        style={{
          width: `${followusWidth}`,
          paddingRight: `0px`
        }}
      >
        <div styleName="follow-us-left">
          <span>ಫಾಲೋ ಮಾಡಿ</span>
          <FollowUs />
        </div>
        <div>
          <BookmarkCommentShareButtons isPVExclusive={isPVExclusive} stickyFollowUs={stickyFollowUs} story={story} />
        </div>
      </div>
    </div>
  );

  const ExclusiveAuthorAndTimeStamp = (
    <>
      <div styleName={`${stickyFollowUs ? "alternateDiv-Sticky-exclusive" : ""}`}></div>
      <div
        style={{ width: followusWidth }}
        ref={followusRef}
        styleName={`exclusive-author-wrapper ${stickyFollowUs ? "sticky-exclusive-wrapper" : ""} `}
      >
        <div styleName="pvexclusive">
          <div styleName="follow-us-left exclusive-follow-us">
            <span>ಫಾಲೋ ಮಾಡಿ</span>
            <FollowUs isPVExclusive={isPVExclusive} />
          </div>
          <div styleName="long-line"></div>
          {hasVisibleAuthors && <Author authors={authorsToDisplay} story={story} clazzName="author-name-image" />}
          <div styleName="exclusive-timestamp">
            <div styleName="date">Published {publishedDateAndTime} IST</div>
            <div styleName="short-line"></div>
            <div styleName="date">Last Updated {lastUpdatedDateAndTime} IST</div>
          </div>
          <div styleName="long-line"></div>
          <BookmarkCommentShareButtons story={story} isPVExclusive={isPVExclusive} stickyFollowUs={stickyFollowUs} />
        </div>
      </div>
    </>
  );

  if (!isMobile && isPVExclusive) {
    return ExclusiveAuthorAndTimeStamp;
  }
  return AuthorTimeStampAndIcons;
};

AuthorAndTimeStamp.propTypes = {
  story: object.isRequired,
  isPVExclusive: bool
};

AuthorAndTimeStamp.defaultProps = {
  isPVExclusive: false
};

export default AuthorAndTimeStamp;
